import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { ViewControls } from './components/ViewControls';
import { KpiTimeChart } from './components/KpiTimeChart';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import {
  fmtMoney, fmtMoneyShort, fmtPeriodo, CHART_COLORS, calcDelta, prevPeriodo,
} from './components/kpiUtils';
import { DollarSign, Plus, Pencil, Trash2, Layers, Wallet, TrendingUp, Banknote } from 'lucide-react';

const KpisIngresos = () => {
  const [data, setData] = useState({ negocios: [], periodos: [], puede_cargar: false });
  const [prestamosGan, setPrestamosGan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('evolucion');
  const [chartType, setChartType] = useState('linea');
  const [mesUno, setMesUno] = useState('');
  const [mesA, setMesA] = useState('');
  const [mesB, setMesB] = useState('');

  const [negDialog, setNegDialog] = useState(false);
  const [negEdit, setNegEdit] = useState(null);
  const [negNombre, setNegNombre] = useState('');
  const [negColor, setNegColor] = useState('#6366F1');

  const [montoDialog, setMontoDialog] = useState(false);
  const [montoNeg, setMontoNeg] = useState('');
  const [montoPer, setMontoPer] = useState('');
  const [montoVal, setMontoVal] = useState('');
  const rootRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/kpis/ingresos');
      setData(r.data);
      const ps = r.data.periodos || [];
      if (ps.length) {
        setMesUno((prev) => prev || ps[ps.length - 1]);
        setMesB((prev) => prev || ps[ps.length - 1]);
        setMesA((prev) => prev || ps[ps.length - 2] || ps[ps.length - 1]);
      }
    } catch (e) {
      toast.error('Error al cargar ingresos');
    } finally {
      setLoading(false);
    }
    try { const rg = await api.get('/kpis/prestamos/detalle'); setPrestamosGan(rg.data.resumen); }
    catch (e) { /* opcional */ }
  };
  useEffect(() => { load(); }, []);

  const { negocios, periodos, puede_cargar } = data;
  const ultimoPeriodo = periodos.length ? periodos[periodos.length - 1] : null;
  const colorOf = (n, i) => n.color || CHART_COLORS[i % CHART_COLORS.length];

  const totalDe = (per) => negocios.reduce((a, n) => a + Number(n.montos?.[per] || 0), 0);
  const totalHistorico = useMemo(
    () => negocios.reduce((acc, n) => acc + Object.values(n.montos || {}).reduce((a, b) => a + Number(b || 0), 0), 0),
    [negocios]
  );

  // ---- datasets por vista ----
  const evo = useMemo(() => {
    const rows = periodos.map((p) => {
      const row = { label: fmtPeriodo(p, true), Total: 0 };
      negocios.forEach((n) => { const v = Number(n.montos?.[p] || 0); row[n.nombre] = v; row.Total += v; });
      return row;
    });
    const src = negocios.map((n, i) => ({ key: n.nombre, name: n.nombre, color: colorOf(n, i) }));
    return { rows, src };
  }, [periodos, negocios]);

  const evoSeries = chartType === 'linea'
    ? [{ key: 'Total', name: 'Total', color: '#0f172a', strong: true }, ...evo.src]
    : evo.src;

  const unMes = useMemo(() => {
    const rows = negocios.map((n, i) => ({ label: n.nombre, monto: Number(n.montos?.[mesUno] || 0), _c: colorOf(n, i) }))
      .filter((x) => x.monto > 0).sort((a, b) => b.monto - a.monto);
    const total = rows.reduce((a, b) => a + b.monto, 0);
    const delta = calcDelta(total, totalDe(prevPeriodo(mesUno)));
    return { rows, total, delta, cellColors: rows.map((r) => r._c) };
  }, [negocios, mesUno]);

  const cmp = useMemo(() => {
    const rows = negocios.map((n) => ({ label: n.nombre, A: Number(n.montos?.[mesA] || 0), B: Number(n.montos?.[mesB] || 0) }))
      .filter((x) => x.A > 0 || x.B > 0);
    const totalA = totalDe(mesA);
    const totalB = totalDe(mesB);
    return { rows, totalA, totalB, delta: calcDelta(totalB, totalA) };
  }, [negocios, mesA, mesB]);

  // ---- acciones admin ----
  const abrirNuevoNegocio = () => { setNegEdit(null); setNegNombre(''); setNegColor('#6366F1'); setNegDialog(true); };
  const abrirEditarNegocio = (n) => { setNegEdit(n); setNegNombre(n.nombre); setNegColor(n.color || '#6366F1'); setNegDialog(true); };
  const guardarNegocio = async () => {
    if (!negNombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      if (negEdit) await api.put(`/kpis/ingresos/negocios/${negEdit.id}`, { nombre: negNombre, color: negColor });
      else await api.post('/kpis/ingresos/negocios', { nombre: negNombre, color: negColor });
      toast.success(negEdit ? 'Fuente actualizada' : 'Fuente creada');
      setNegDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };
  const eliminarNegocio = async (n) => {
    if (!window.confirm(`¿Eliminar la fuente "${n.nombre}" y todos sus montos?`)) return;
    try { await api.delete(`/kpis/ingresos/negocios/${n.id}`); toast.success('Fuente eliminada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo eliminar'); }
  };
  const abrirMonto = (negId, per) => {
    const nid = negId || negocios[0]?.id || '';
    setMontoNeg(nid);
    setMontoPer(per || ultimoPeriodo || new Date().toISOString().slice(0, 7));
    const n = negocios.find((x) => x.id === nid);
    setMontoVal(per && n ? String(n.montos?.[per] ?? '') : '');
    setMontoDialog(true);
  };
  const guardarMonto = async () => {
    if (!montoNeg || !/^\d{4}-\d{2}$/.test(montoPer) || montoVal === '') { toast.error('Completá fuente, período y monto'); return; }
    try { await api.post('/kpis/ingresos/monto', { negocio_id: montoNeg, periodo: montoPer, monto: Number(montoVal) }); toast.success('Monto guardado'); setMontoDialog(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar el monto'); }
  };
  const eliminarMonto = async (negId, per) => {
    try { await api.post('/kpis/ingresos/monto/eliminar', { negocio_id: negId, periodo: per }); toast.success('Monto eliminado'); load(); }
    catch (e) { toast.error('Error al eliminar'); }
  };

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-ingresos-loading">Cargando ingresos…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-ingresos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10"><DollarSign className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Ingresos</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-ingresos" /></div>
            <p className="text-sm text-slate-500">Carga manual mensual por fuente de negocio</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="ingresos" testId="ingresos-help-btn" />
          <SectionExportButton title="Ingresos" targetRef={rootRef} testId="ingresos-export-btn" />
          {puede_cargar && (
            <>
              <Button onClick={() => abrirMonto()} className="bg-indigo-600 hover:bg-indigo-700 active:scale-95 transition-transform" data-testid="ingresos-cargar-monto-btn">
                <Plus className="h-4 w-4 mr-1" /> Cargar monto
              </Button>
              <Button variant="outline" onClick={abrirNuevoNegocio} data-testid="ingresos-nuevo-negocio-btn">
                <Layers className="h-4 w-4 mr-1" /> Nueva fuente
              </Button>
            </>
          )}
        </div>
      </div>

      {/* KPIs por vista */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {view === 'comparar' ? (
          <>
            <StatCard label={`Total ${fmtPeriodo(mesA)}`} value={fmtMoney(cmp.totalA)} icon={DollarSign} accent="#4f46e5" testId="ingresos-kpi-a" />
            <StatCard label={`Total ${fmtPeriodo(mesB)}`} value={fmtMoney(cmp.totalB)} icon={DollarSign} accent="#0ea5e9" testId="ingresos-kpi-b" />
            <StatCard label="Variación" value={cmp.delta ? `${cmp.delta.pct >= 0 ? '+' : ''}${cmp.delta.pct.toFixed(1)}%` : '—'} icon={TrendingUp} accent="#10b981" delta={cmp.delta} deltaLabel={`${fmtPeriodo(mesA)} → ${fmtPeriodo(mesB)}`} testId="ingresos-kpi-var" />
          </>
        ) : view === 'un-mes' ? (
          <>
            <StatCard label={`Total ${fmtPeriodo(mesUno)}`} value={fmtMoney(unMes.total)} icon={DollarSign} accent="#4f46e5" delta={unMes.delta} deltaLabel="vs mes anterior" testId="ingresos-kpi-mes" />
            <StatCard label="Fuentes con ingreso" value={unMes.rows.length} icon={Layers} accent="#6366f1" testId="ingresos-kpi-fuentes-mes" />
            <StatCard label="Fuente principal" value={unMes.rows[0]?.label || '—'} icon={Wallet} accent="#0ea5e9" deltaLabel={unMes.rows[0] ? fmtMoney(unMes.rows[0].monto) : ''} testId="ingresos-kpi-top" />
          </>
        ) : (
          <>
            <StatCard label={`Último período${ultimoPeriodo ? ` · ${fmtPeriodo(ultimoPeriodo)}` : ''}`} value={fmtMoney(totalDe(ultimoPeriodo))} icon={DollarSign} accent="#4f46e5" delta={calcDelta(totalDe(ultimoPeriodo), totalDe(prevPeriodo(ultimoPeriodo)))} deltaLabel="MoM" testId="ingresos-kpi-ultimo" />
            <StatCard label="Total histórico" value={fmtMoney(totalHistorico)} icon={Wallet} accent="#10b981" testId="ingresos-kpi-historico" />
            <StatCard label="Fuentes activas" value={negocios.length} icon={Layers} accent="#6366f1" testId="ingresos-kpi-fuentes" />
          </>
        )}
      </div>

      {/* Ganancia de préstamos (realizada) */}
      {prestamosGan && prestamosGan.total_esperado > 0 && (
        <Card className="p-5 mb-6 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200" data-testid="ingresos-prestamos-ganancia">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-full bg-emerald-600 shrink-0"><Banknote className="text-white" size={20} /></div>
              <div>
                <h2 className="text-base font-semibold text-emerald-900">Ganancia de préstamos (realizada)</h2>
                <p className="text-xs text-emerald-700/70 mt-0.5">Interés efectivamente cobrado por cuotas cerradas + punitorios. {prestamosGan.n_con_pagos}/{prestamosGan.n_prestamos} préstamos con cobros.</p>
                <a href="/m/kpis/kpis-prestamos" className="text-xs font-medium text-emerald-700 hover:text-emerald-900 underline decoration-dotted mt-1 inline-block" data-testid="ingresos-prestamos-link">Ver detalle en Préstamos →</a>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-medium">Realizado</p>
                <p className="text-2xl font-bold font-mono text-emerald-700 tabular-nums" data-testid="ingresos-ganancia-realizado">{fmtMoney(prestamosGan.total_realizado)}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Esperado</p>
                <p className="text-2xl font-bold font-mono text-slate-500 tabular-nums">{fmtMoney(prestamosGan.total_esperado)}</p>
                <p className="text-xs text-emerald-800 mt-0.5">{prestamosGan.pct_global}% realizado</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Chart card */}
      <Card className="p-5 lg:p-6 mb-8" data-testid="ingresos-chart-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
          <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">
            {view === 'comparar' ? 'Comparación por fuente' : view === 'un-mes' ? `Composición · ${fmtPeriodo(mesUno)}` : 'Evolución de ingresos'}
          </h2>
          <ViewControls
            view={view} onView={setView} chartType={chartType} onChartType={setChartType}
            periodos={periodos} mesUno={mesUno} onMesUno={setMesUno} mesA={mesA} onMesA={setMesA} mesB={mesB} onMesB={setMesB}
            showChartType={view === 'evolucion'}
          />
        </div>

        {view === 'evolucion' && (
          <KpiTimeChart data={evo.rows} series={evoSeries} type={chartType} stacked={chartType !== 'linea'}
            valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} testId="ingresos-chart-evolucion" />
        )}
        {view === 'un-mes' && (
          <KpiTimeChart data={unMes.rows} xKey="label" series={[{ key: 'monto', name: 'Ingreso', color: '#4f46e5' }]}
            type="barras" barLayout="vertical" cellColors={unMes.cellColors} height={Math.max(220, unMes.rows.length * 46)}
            valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} showLegend={false} testId="ingresos-chart-mes" />
        )}
        {view === 'comparar' && (
          <KpiTimeChart data={cmp.rows} xKey="label"
            series={[{ key: 'A', name: fmtPeriodo(mesA), color: '#4f46e5' }, { key: 'B', name: fmtPeriodo(mesB), color: '#0ea5e9' }]}
            type="barras" barLayout="vertical" height={Math.max(240, cmp.rows.length * 52)}
            valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} testId="ingresos-chart-comparar" />
        )}
      </Card>

      {/* Matriz editable */}
      <Card className="p-0 overflow-hidden" data-testid="ingresos-matriz">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">Detalle mensual por fuente</h2>
          <p className="text-xs text-slate-400 mt-0.5">{puede_cargar ? 'Hacé clic en un monto para editarlo.' : 'Solo lectura'}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <th className="text-left font-medium px-4 py-2.5 sticky left-0 bg-slate-50 dark:bg-slate-800">Fuente</th>
                {periodos.map((p) => <th key={p} className="text-right font-medium px-4 py-2.5 whitespace-nowrap">{fmtPeriodo(p, true)}</th>)}
                {puede_cargar && <th className="px-3 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {negocios.map((n, i) => (
                <tr key={n.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ingresos-row-${n.id}`}>
                  <td className="px-4 py-2.5 sticky left-0 bg-white dark:bg-slate-900">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorOf(n, i) }} />
                      <span className="font-medium text-slate-700 dark:text-slate-200">{n.nombre}</span>
                      {n.es_fijo && <span className="text-[10px] text-slate-400 border border-slate-200 rounded px-1">fija</span>}
                    </div>
                  </td>
                  {periodos.map((p) => {
                    const v = n.montos?.[p];
                    return (
                      <td key={p} className="px-4 py-2.5 text-right whitespace-nowrap tabular-nums">
                        {puede_cargar ? (
                          <button onClick={() => abrirMonto(n.id, p)} className="hover:underline decoration-dotted text-slate-700 dark:text-slate-200" data-testid={`ingresos-cell-${n.id}-${p}`}>
                            {v ? fmtMoney(v) : <span className="text-slate-300">+</span>}
                          </button>
                        ) : (v ? fmtMoney(v) : <span className="text-slate-300">—</span>)}
                      </td>
                    );
                  })}
                  {puede_cargar && (
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <button onClick={() => abrirEditarNegocio(n)} className="text-slate-400 hover:text-indigo-600 mr-2" title="Editar fuente" data-testid={`ingresos-edit-${n.id}`}><Pencil className="h-4 w-4 inline" /></button>
                      {!n.es_fijo && <button onClick={() => eliminarNegocio(n)} className="text-slate-400 hover:text-rose-600" title="Eliminar fuente" data-testid={`ingresos-del-${n.id}`}><Trash2 className="h-4 w-4 inline" /></button>}
                    </td>
                  )}
                </tr>
              ))}
              {negocios.length === 0 && <tr><td colSpan={99} className="px-4 py-8 text-center text-slate-400">No hay fuentes cargadas.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog fuente */}
      <Dialog open={negDialog} onOpenChange={setNegDialog}>
        <DialogContent data-testid="ingresos-negocio-dialog">
          <DialogHeader><DialogTitle>{negEdit ? 'Editar fuente' : 'Nueva fuente de ingreso'}</DialogTitle>
            <DialogDescription>Definí el nombre y el color con el que se muestra la fuente en los gráficos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nombre</Label>
              <Input value={negNombre} onChange={(e) => setNegNombre(e.target.value)} placeholder="Ej: Comisiones tarjetas" data-testid="ingresos-negocio-nombre" /></div>
            <div><Label>Color</Label>
              <input type="color" value={negColor} onChange={(e) => setNegColor(e.target.value)} className="h-10 w-16 rounded border border-slate-200 ml-2 align-middle" data-testid="ingresos-negocio-color" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNegDialog(false)}>Cancelar</Button>
            <Button onClick={guardarNegocio} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ingresos-negocio-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog monto */}
      <Dialog open={montoDialog} onOpenChange={setMontoDialog}>
        <DialogContent data-testid="ingresos-monto-dialog">
          <DialogHeader><DialogTitle>Cargar / editar monto</DialogTitle>
            <DialogDescription>Ingresá el monto mensual (ARS) de la fuente para el período seleccionado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Fuente</Label>
              <Select value={montoNeg} onValueChange={setMontoNeg}>
                <SelectTrigger data-testid="ingresos-monto-negocio"><SelectValue placeholder="Elegí una fuente" /></SelectTrigger>
                <SelectContent>{negocios.map((n) => <SelectItem key={n.id} value={n.id}>{n.nombre}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Período</Label>
              <Input type="month" value={montoPer} onChange={(e) => setMontoPer(e.target.value)} data-testid="ingresos-monto-periodo" /></div>
            <div><Label>Monto (ARS)</Label>
              <Input type="number" step="0.01" value={montoVal} onChange={(e) => setMontoVal(e.target.value)} placeholder="0.00" data-testid="ingresos-monto-valor" /></div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            {montoNeg && /^\d{4}-\d{2}$/.test(montoPer) && negocios.find((n) => n.id === montoNeg)?.montos?.[montoPer] != null && (
              <Button variant="ghost" className="text-rose-600" onClick={() => { eliminarMonto(montoNeg, montoPer); setMontoDialog(false); }} data-testid="ingresos-monto-eliminar">Borrar monto</Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setMontoDialog(false)}>Cancelar</Button>
              <Button onClick={guardarMonto} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ingresos-monto-guardar">Guardar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KpisIngresos;
